import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronRight, ChevronLeft, Upload, Search, Sparkles, Download, Check } from 'lucide-react';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import CompactBeforeAfterSlider from '../components/CompactBeforeAfterSlider';
import { OrderServiceCTA } from '../components/OrderServiceCTA';
import { cn } from "@/lib/utils";

const PhotoRestoration = () => {
  // Carousel state
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Check scroll state
  const checkScrollState = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollState();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', checkScrollState);
      return () => carousel.removeEventListener('scroll', checkScrollState);
    }
  }, []);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.querySelector('.pricing-card')?.offsetWidth || 340;
      const gap = 24;
      const scrollAmount = direction === 'left' ? -(cardWidth + gap) : (cardWidth + gap);
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Pricing packages
  const pricingPackages = [
    {
      name: "Essential Restore",
      price: "$25",
      description: "For light cleanup and minor corrections.",
      features: [
        "Dust & scratch removal",
        "Minor blemish repair",
        "Basic color correction",
        "Light tear repair",
        "High-resolution export (original size only)"
      ],
      note: "Best for images with minimal wear. Heavily faded photos may qualify for Signature Restoration.",
      featured: false,
      elite: false
    },
    {
      name: "Signature Restore",
      price: "$60",
      launchPrice: "$42",
      description: "For noticeable damage and professional refinement.",
      features: [
        "Moderate tear repair",
        "Fading & discoloration correction",
        "Facial detail refinement",
        "Noise reduction",
        "Contrast & tonal balancing",
        "Print-ready export"
      ],
      featured: true,
      elite: false
    },
    {
      name: "Heirloom Restoration",
      price: "$120+",
      description: "For severely damaged or historic images.",
      features: [
        "Heavy damage repair",
        "Missing area reconstruction",
        "Advanced facial restoration",
        "Structural correction",
        "Optional colorization",
        "Gallery-scale print preparation"
      ],
      note: "Final pricing confirmed after image review for complex restorations.",
      featured: false,
      elite: true
    }
  ];

  // Hero slider images - main showcase
  // LEFT = damaged sepia vintage photo (BEFORE), RIGHT = restored clean photo (AFTER)
  const heroImages = {
    before: "https://customer-assets.emergentagent.com/job_e9a01186-23de-4e11-a93b-b3b28ef3cbe6/artifacts/7nc6bd44_Scan.png",
    after: "https://customer-assets.emergentagent.com/job_e9a01186-23de-4e11-a93b-b3b28ef3cbe6/artifacts/t777a08b_ChatGPT%20Image%20Feb%2012%2C%202026%2C%2001_02_46%20PM.png"
  };

  // Vintage Photo Repair slider images - BEFORE = original damaged, AFTER = restored
  const vintageRepairImages = {
    before: "https://customer-assets.emergentagent.com/job_cd29670c-5b05-4991-9683-45d79e4aac46/artifacts/em16lhsy_Scan2.png",
    after: "https://customer-assets.emergentagent.com/job_cd29670c-5b05-4991-9683-45d79e4aac46/artifacts/s1cziftl_ComfyUI_00220_.png"
  };

  // Noise & Artifact Removal slider images - BEFORE = original noisy, AFTER = restored
  const noiseRemovalImages = {
    before: "https://customer-assets.emergentagent.com/job_cd29670c-5b05-4991-9683-45d79e4aac46/artifacts/fevhpu2i_Roseville-Pool.jpg",
    after: "https://customer-assets.emergentagent.com/job_cd29670c-5b05-4991-9683-45d79e4aac46/artifacts/5xckwx7r_Roseville-Pool-Restored.png"
  };

  // Gallery-Scale Print Preparation slider images
  const galleryPrintImages = {
    before: "https://customer-assets.emergentagent.com/job_cd29670c-5b05-4991-9683-45d79e4aac46/artifacts/b4c9lnc3_IMG_8609.jpeg",
    after: "https://customer-assets.emergentagent.com/job_cd29670c-5b05-4991-9683-45d79e4aac46/artifacts/vv0fziqp_ART.png"
  };

  const services = [
    {
      title: "Vintage Photo Repair",
      desc: "Restore damaged, torn, or faded photographs to their original beauty.",
      hasSlider: true,
      sliderType: "vintage"
    },
    {
      title: "Noise & Artifact Removal",
      desc: "Reduce grain, compression artifacts, and low-light noise while preserving natural detail — then upscale for modern clarity.",
      hasSlider: true,
      sliderType: "noise"
    }
  ];

  const processSteps = [
    { num: "01", title: "Submit Your Image", icon: Upload, desc: "Upload your photo securely through our intake form." },
    { num: "02", title: "Precision Evaluation", icon: Search, desc: "We assess damage, resolution, and restoration scope." },
    { num: "03", title: "AI + Manual Refinement", icon: Sparkles, desc: "Advanced AI paired with hand-crafted artistic control." },
    { num: "04", title: "High-Resolution Delivery", icon: Download, desc: "Receive print-ready files in your preferred format." }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-[#0f1724]">
      
      {/* ════════════════════════════════════════════════════════════════════
          HERO SECTION - Interactive Before/After Slider (Centered Container)
          ════════════════════════════════════════════════════════════════════ */}
      <section className="bg-[#0f1724] pt-20 md:pt-24 pb-8">
        <div className="container mx-auto px-6">
          {/* Constrained slider container */}
          <div className="max-w-5xl mx-auto">
            {/* Interactive Before/After Slider - Before=damaged/sepia, After=restored/clean */}
            <BeforeAfterSlider
              beforeSrc={heroImages.before}
              afterSrc={heroImages.after}
              altBefore="Original faded vintage photo"
              altAfter="Restored and enhanced photo"
              initialPercent={50}
            />
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          HEADLINE SECTION - Text Below Slider
          ════════════════════════════════════════════════════════════════════ */}
      <section className="bg-[#0f1724] pb-10 md:pb-12">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          {/* Eyebrow */}
          <p className="text-[#C9A962] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
            Photo Restoration & Enhancement
          </p>
          
          {/* H1 Headline */}
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white tracking-wide mb-4">
            RESTORE. REFINE. PRESERVE.
          </h1>
          
          {/* Subheadline */}
          <p className="text-lg md:text-xl text-gray-300 mb-2">
            Luxury Photo Restoration & Precision Enhancement
          </p>
          
          {/* Helper line */}
          <p className="text-sm text-[#C9A962] italic tracking-wide">
            Slide to reveal the transformation
          </p>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          SECTION 1 - Service Overview
          ════════════════════════════════════════════════════════════════════ */}
      <section className="py-10 md:py-12 bg-[#0f1724]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-12 h-[1px] bg-[#C9A962]/40 mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-white/70 leading-relaxed">
            Our restoration services combine cutting-edge AI technology with meticulous hand-refinement to bring your cherished photographs back to life. Whether you're preserving a family heirloom, preparing archival prints, or enhancing modern images for large-format display — we deliver museum-quality results with the care your memories deserve.
          </p>
          <div className="w-12 h-[1px] bg-[#C9A962]/40 mx-auto mt-6"></div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          SECTION 2 - What We Restore (Luxury Showcase Blocks)
          ════════════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-[#0f1724] to-[#141c2b]">
        <div className="max-w-6xl mx-auto px-6">
          
          {/* Section Header */}
          <div className="text-center mb-12">
            <p className="text-[#C9A962] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              Capabilities
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              What We Restore
            </h2>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              SHOWCASE BLOCK 1 — Vintage Photo Repair
              Text Left (40%) | Visual Right (60%)
              ═══════════════════════════════════════════════════════════════ */}
          <div className="mb-16">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              
              {/* Text Column */}
              <div className="lg:col-span-5 order-2 lg:order-1">
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-6">
                  Vintage Photo Repair
                </h3>
                <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8">
                  Meticulously restore aged, torn, or faded photographs with precision care — preserving their authenticity while bringing lost detail, tone, and clarity back to life.
                </p>
                
                {/* Bullet Highlights */}
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Tear & scratch reconstruction</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Tone & contrast recovery</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Archival-quality enhancement</span>
                  </li>
                </ul>
              </div>

              {/* Visual Column */}
              <div className="lg:col-span-7 order-1 lg:order-2">
                <div className="relative">
                  {/* Subtle background glow */}
                  <div 
                    className="absolute inset-0 -m-8 rounded-3xl opacity-30"
                    style={{
                      background: 'radial-gradient(ellipse at center, rgba(15, 23, 36, 0.8) 0%, transparent 70%)'
                    }}
                  />
                  
                  {/* Slider Container */}
                  <div className="relative max-w-[600px] mx-auto lg:mx-0 lg:ml-auto">
                    <CompactBeforeAfterSlider
                      beforeSrc={vintageRepairImages.before}
                      afterSrc={vintageRepairImages.after}
                      altBefore="Faded vintage family photo"
                      altAfter="Restored and color-corrected photo"
                      initialPercent={50}
                      aspectRatio="4/3"
                    />
                    <p className="text-[#C9A962]/50 text-[10px] tracking-[0.2em] uppercase text-center mt-4">
                      Drag to compare
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Subtle Divider */}
          <div className="flex items-center justify-center mb-16">
            <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-[#C9A962]/30 to-transparent"></div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════
              SHOWCASE BLOCK 2 — Noise & Artifact Removal
              Visual Left (60%) | Text Right (40%) — Reversed for rhythm
              ═══════════════════════════════════════════════════════════════ */}
          <div>
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              
              {/* Visual Column */}
              <div className="lg:col-span-7 order-1">
                <div className="relative">
                  {/* Subtle background glow */}
                  <div 
                    className="absolute inset-0 -m-8 rounded-3xl opacity-30"
                    style={{
                      background: 'radial-gradient(ellipse at center, rgba(15, 23, 36, 0.8) 0%, transparent 70%)'
                    }}
                  />
                  
                  {/* Slider Container */}
                  <div className="relative max-w-[600px] mx-auto lg:mx-0">
                    <CompactBeforeAfterSlider
                      beforeSrc={noiseRemovalImages.before}
                      afterSrc={noiseRemovalImages.after}
                      altBefore="Noisy low-light pool photo"
                      altAfter="Cleaned and upscaled photo"
                      initialPercent={50}
                      aspectRatio="4/3"
                    />
                    <p className="text-[#C9A962]/50 text-[10px] tracking-[0.2em] uppercase text-center mt-4">
                      Drag to compare
                    </p>
                  </div>
                </div>
              </div>

              {/* Text Column */}
              <div className="lg:col-span-5 order-2">
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-6">
                  Noise & Artifact Removal
                </h3>
                <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8">
                  Reduce grain, compression artifacts, and low-light noise while preserving natural detail — then upscale for modern clarity.
                </p>
                
                {/* Bullet Highlights */}
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Low-light recovery</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Detail refinement</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Intelligent upscaling</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* ─────────────────────────────────────────────────────────
              SHOWCASE BLOCK 3 — Color Fading & Tone Reconstruction (Text Left, Slider Right)
              ───────────────────────────────────────────────────────── */}
          <div className="py-12">
            {/* Gold divider */}
            <div className="w-16 h-px bg-[#C9A962]/30 mx-auto mb-12"></div>
            
            <div className="grid md:grid-cols-2 gap-10 items-center">
              {/* Text Content - Left */}
              <div>
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">
                  Color Fading & Tone Reconstruction
                </h3>
                <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8">
                  Over time, chemical degradation and improper storage can cause photos to shift toward red, magenta, or yellow tones. We carefully reconstruct natural skin tones, restore contrast, and rebalance the image while preserving authentic detail.
                </p>
                
                {/* Bullet Highlights */}
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Severe color shift correction</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Natural skin tone restoration</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Contrast & tonal rebalancing</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Archival-quality finishing</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                    <span className="text-white/50 text-sm">Print-ready output</span>
                  </li>
                </ul>
              </div>
              
              {/* Compact Slider - Right */}
              {/* LOCKED: walter1.png = BEFORE (faded magenta), walterafter.png = AFTER (restored neutral) */}
              <div className="rounded-2xl overflow-hidden bg-[#141c2b]">
                <CompactBeforeAfterSlider 
                  beforeSrc="https://customer-assets.emergentagent.com/job_ff48cbd1-cea0-46af-a350-95ed0a86fa73/artifacts/xptwpiar_walter1.png"
                  afterSrc="https://customer-assets.emergentagent.com/job_ff48cbd1-cea0-46af-a350-95ed0a86fa73/artifacts/39otgjny_walterafter.png"
                  aspectRatio="3/4"
                />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          SECTION - Investment / Professional Restoration Pricing
          ════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-24 bg-[#0f1724]">
        <div className="max-w-6xl mx-auto px-6">
          
          {/* Section Header */}
          <div className="text-center mb-10">
            <p className="text-[#C9A962] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              Investment
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Professional Restoration Pricing
            </h2>
            <p className="text-white/50 text-lg max-w-xl mx-auto">
              Precision-crafted restoration with a limited launch offer for new clients.
            </p>
          </div>

          {/* Launch Banner */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="py-4 px-6 rounded-xl bg-[#C9A962]/10 border border-[#C9A962]/20 text-center">
              <p className="text-[#C9A962] text-sm md:text-base">
                <span className="font-semibold">Limited Launch Offer</span>
                <span className="mx-2">—</span>
                <span>Signature Restoration <span className="font-semibold">$42</span> <span className="text-white/40">(normally $60)</span></span>
              </p>
              <p className="text-white/40 text-xs mt-1">Valid for the first 25 clients.</p>
            </div>
          </div>

          {/* Pricing Carousel */}
          <div className="relative mb-12">
            {/* Previous Button */}
            <button
              onClick={() => scrollCarousel('left')}
              disabled={!canScrollLeft}
              aria-label="Previous package"
              className={cn(
                "absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300",
                "bg-[#141c2b] border border-white/10 shadow-lg",
                canScrollLeft 
                  ? "text-white hover:border-[#C9A962]/50 hover:text-[#C9A962]" 
                  : "text-white/20 cursor-not-allowed",
                "hidden md:flex -translate-x-1/2 lg:-translate-x-full lg:left-2"
              )}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Next Button */}
            <button
              onClick={() => scrollCarousel('right')}
              disabled={!canScrollRight}
              aria-label="Next package"
              className={cn(
                "absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300",
                "bg-[#141c2b] border border-white/10 shadow-lg",
                canScrollRight 
                  ? "text-white hover:border-[#C9A962]/50 hover:text-[#C9A962]" 
                  : "text-white/20 cursor-not-allowed",
                "hidden md:flex translate-x-1/2 lg:translate-x-full lg:right-2"
              )}
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Carousel */}
            <div
              ref={carouselRef}
              className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 px-1"
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {pricingPackages.map((pkg, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "pricing-card flex-shrink-0 snap-center",
                    "w-[320px] sm:w-[360px]",
                    "rounded-2xl border transition-all duration-300 overflow-hidden",
                    pkg.featured 
                      ? "border-[#C9A962] bg-[#141c2b] shadow-xl shadow-[#C9A962]/10" 
                      : pkg.elite
                        ? "border-[#C9A962]/30 bg-gradient-to-b from-[#141c2b] to-[#0f1724]"
                        : "border-white/10 bg-[#141c2b]"
                  )}
                >
                  {/* Badge */}
                  {pkg.featured && (
                    <div className="bg-[#C9A962] text-[#0f1724] text-xs font-semibold tracking-[0.2em] uppercase text-center py-2">
                      Most Popular
                    </div>
                  )}
                  {pkg.elite && (
                    <div className="bg-[#C9A962]/20 text-[#C9A962] text-xs font-semibold tracking-[0.2em] uppercase text-center py-2">
                      Elite
                    </div>
                  )}
                  
                  <div className="p-6 flex flex-col min-h-[480px]">
                    <h3 className="font-serif text-2xl text-white mb-2">{pkg.name}</h3>
                    
                    {/* Price */}
                    <div className="mb-3">
                      {pkg.launchPrice ? (
                        <div className="flex items-baseline gap-2">
                          <span className="text-[#C9A962] text-3xl font-bold">{pkg.launchPrice}</span>
                          <span className="text-white/40 text-lg line-through">{pkg.price}</span>
                          <span className="text-[#C9A962]/70 text-xs uppercase tracking-wide">Limited Time</span>
                        </div>
                      ) : (
                        <span className="text-[#C9A962] text-3xl font-bold">{pkg.price}</span>
                      )}
                    </div>
                    
                    <p className="text-white/50 text-sm mb-6">{pkg.description}</p>
                    
                    <ul className="space-y-2.5 mb-6 flex-grow">
                      {pkg.features.map((feature, fIdx) => (
                        <li key={fIdx} className="flex items-start gap-3">
                          <Check className="w-4 h-4 text-[#C9A962] mt-0.5 flex-shrink-0" />
                          <span className="text-white/70 text-sm leading-snug">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link 
                      to="/order/photo-restoration"
                      className={cn(
                        "block text-center py-3 rounded-lg font-semibold transition-all",
                        pkg.featured || pkg.elite
                          ? "bg-[#C9A962] text-[#0f1724] hover:bg-[#C9A962]/90"
                          : "bg-white/10 text-white hover:bg-white/20"
                      )}
                      data-testid={`pricing-cta-${idx}`}
                    >
                      Start Restoration
                    </Link>
                    
                    {pkg.note && (
                      <p className="text-white/40 text-xs mt-4 pt-4 border-t border-white/10 leading-relaxed text-center">
                        {pkg.note}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Navigation Dots */}
            <div className="flex justify-center gap-2 mt-6 md:hidden">
              {pricingPackages.map((_, idx) => (
                <button
                  key={idx}
                  aria-label={`Go to package ${idx + 1}`}
                  onClick={() => {
                    if (carouselRef.current) {
                      const card = carouselRef.current.querySelector('.pricing-card');
                      if (card) {
                        const cardWidth = card.offsetWidth + 24;
                        carouselRef.current.scrollTo({ left: cardWidth * idx, behavior: 'smooth' });
                      }
                    }
                  }}
                  className="w-2 h-2 rounded-full bg-white/20 hover:bg-[#C9A962]/50 transition-colors"
                />
              ))}
            </div>
          </div>

          {/* Bundle Pricing */}
          <div className="text-center mb-10">
            <h3 className="font-serif text-xl text-white mb-4">Restore More & Save</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              <div className="flex items-baseline gap-2">
                <span className="text-white/70">3 Images</span>
                <span className="text-white/30">—</span>
                <span className="text-[#C9A962] font-semibold">$120</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-white/70">5 Images</span>
                <span className="text-white/30">—</span>
                <span className="text-[#C9A962] font-semibold">$185</span>
              </div>
            </div>
          </div>

          {/* Add-ons */}
          <div className="text-center mb-8">
            <p className="text-white/40 text-sm mb-3">Add-Ons</p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <span className="text-white/60 text-sm">Colorization <span className="text-[#C9A962]">+$50</span></span>
              <span className="text-white/30">•</span>
              <span className="text-white/60 text-sm">Poster-Size Upscaling <span className="text-[#C9A962]">+$25</span></span>
              <span className="text-white/30 hidden sm:inline">•</span>
              <span className="text-white/60 text-sm">48-Hour Express Turnaround <span className="text-[#C9A962]">+$30</span></span>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-center text-white/30 text-xs max-w-xl mx-auto">
            Final pricing may adjust based on damage severity after image review. Clients are notified before any additional work begins.
          </p>

        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          SECTION 2B - Gallery-Scale Print Preparation
          ════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-[#0f1724]">
        <div className="max-w-6xl mx-auto px-6">
          
          {/* Section Header */}
          <div className="text-center mb-12">
            <p className="text-[#C9A962] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              Print Preparation
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Gallery-Scale Print Preparation
            </h2>
            <p className="text-white/50 text-base md:text-lg max-w-2xl mx-auto">
              Transform everyday photos into statement wall art — optimized for large-format display without sacrificing natural detail.
            </p>
          </div>

          {/* Two-Column Layout */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Text Column (45%) */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8">
                Most images look beautiful on a phone — but lose clarity when printed large. We upscale and refine your photos for 16×20, 24×36, and gallery-scale wall art while preserving natural texture, fur detail, facial features, and tonal depth.
              </p>
              
              {/* Bullet Highlights */}
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                  <span className="text-white/50 text-sm">Optimized for large-format prints</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                  <span className="text-white/50 text-sm">Texture-preserving upscaling</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                  <span className="text-white/50 text-sm">Natural detail refinement</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A962]"></span>
                  <span className="text-white/50 text-sm">Print-ready resolution delivery</span>
                </li>
              </ul>
            </div>

            {/* Visual Column (55%) */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="relative">
                {/* Subtle background glow */}
                <div 
                  className="absolute inset-0 -m-8 rounded-3xl opacity-40"
                  style={{
                    background: 'radial-gradient(ellipse at center, rgba(15, 23, 36, 0.9) 0%, transparent 70%)'
                  }}
                />
                
                {/* Static Image Display */}
                <div className="relative max-w-[600px] mx-auto lg:mx-0 lg:ml-auto">
                  <div 
                    className="overflow-hidden rounded-xl shadow-lg"
                    style={{
                      border: '1px solid rgba(201, 169, 98, 0.15)',
                      boxShadow: '0 4px 30px rgba(0,0,0,0.4)'
                    }}
                  >
                    <img 
                      src={galleryPrintImages.after}
                      alt="Gallery-ready upscaled art print"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  <p className="text-[#C9A962]/50 text-[10px] tracking-[0.2em] uppercase text-center mt-4">
                    Optimized for 24×36 print
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          SECTION 3 - Our Process
          ════════════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-16 bg-[#141c2b]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[#C9A962] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              How It Works
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              Our Process
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 md:gap-4">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Connector line (desktop) */}
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] right-0 h-[1px] bg-gradient-to-r from-[#C9A962]/40 to-[#C9A962]/10"></div>
                )}
                
                <div className="text-center md:text-left">
                  {/* Step number */}
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#C9A962]/10 border border-[#C9A962]/20 mb-5">
                    <step.icon className="w-8 h-8 text-[#C9A962]" />
                  </div>
                  
                  <p className="text-[#C9A962] text-xs font-bold tracking-widest mb-2">{step.num}</p>
                  <h3 className="text-white font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          SECTION 5 - Call To Action
          ════════════════════════════════════════════════════════════════════ */}
      <section className="py-14 md:py-20 bg-gradient-to-b from-[#0f1724] to-[#0a0e14]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          {/* Decorative line */}
          <div className="w-16 h-[1px] bg-[#C9A962]/40 mx-auto mb-8"></div>
          
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-5">
            Ready to Restore Your Image?
          </h2>
          
          <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
            Let us bring your treasured photographs back to life with precision and care.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <OrderServiceCTA 
              serviceKey="photo_restoration"
              variant="gold"
              size="lg"
              label="Start Your Restoration"
            />
            <Button 
              asChild 
              variant="outline"
              className="border-[#C9A962]/30 text-[#C9A962] hover:bg-[#C9A962]/10 hover:border-[#C9A962]/50 rounded-lg h-13 px-10 text-sm uppercase tracking-[0.15em] font-medium transition-all bg-transparent"
            >
              <Link to="/contact">
                Request a Private Quote
              </Link>
            </Button>
          </div>
          
          {/* Decorative line */}
          <div className="w-16 h-[1px] bg-[#C9A962]/40 mx-auto mt-10"></div>
        </div>
      </section>

      {/* Footer gradient transition */}
      <div className="h-16 bg-gradient-to-b from-[#0a0e14] to-[#050816]"></div>
    </div>
  );
};

export default PhotoRestoration;
